import axios from "axios";
import { getURLFromPieces } from "src/shared";
import {
  AML_CHECK_ENDPOINT,
  SEND_EMAILS_ENDPOINT,
  VALIDATE_AML_ENDPOINT,
} from "src/constants";

/**
 * Vuex actions that call the backend REST API.
 * These are pure HTTP actions — no wallet / blockchain interaction.
 * Spread into the root actions object so dispatch names stay unchanged.
 */
export const backendActions = {
  async getProjectsFromBackend({ state, commit }, payload) {
    try {
      commit("setIsLoading", true);
      let params = new URLSearchParams();
      if (!!payload) {
        if (payload?.paged) {
          params.append("page", payload.page);
          params.append("limit", payload.limit);
        }
        if (payload?.includeStats === false) {
          params.append("include_stats", "false");
        }
        if (payload?.listed === false) {
          params.append("listed", payload.listed);
        }
        if (payload?.is_hidden === true) {
          params.append("is_hidden", payload.is_hidden);
        }
      }
      const response = await axios.get(getURLFromPieces(payload?.endpoint), {
        params,
      });
      const allRaw = response.data.projects || [];
      const projects = allRaw.map((it) => {
        const data = it.data && typeof it.data === "object" ? it.data : null;
        return {
          ...it,
          data,
        };
      });
      commit("setProjects", projects);
      return { ...response.data, projects, stats: response.data.stats || null };
    } catch (error) {
      commit("setServerError", error);
    } finally {
      commit("setIsLoading", false);
    }
  },

  async fetchUserRecords({ state, commit }) {
    if (!state.currentAccount?.address) {
      return null;
    }
    const cached = state.userRecordsCache;
    const addr = state.currentAccount.address.toLowerCase();
    if (cached && cached.address === addr) {
      return cached.records;
    }
    try {
      const { data } = await axios.get(
        getURLFromPieces("users", addr, "records")
      );
      const payload = data || {};
      payload.investments = payload.investments || [];
      payload.mints = payload.mints || [];
      payload.claims = payload.claims || [];
      payload.refunds = payload.refunds || [];
      payload.erc1155_balances = payload.erc1155_balances || [];
      commit("setUserRecordsCache", {
        address: addr,
        fetchedAt: Date.now(),
        records: payload,
      });
      return payload;
    } catch (err) {
      console.error("fetchUserRecords failed", err);
      throw err;
    }
  },

  async getUserDashboard({ state }) {
    const wallet = state.currentAccount?.address;
    if (!wallet) return null;
    try {
      const { data } = await axios.get(
        getURLFromPieces("users", wallet, "dashboard")
      );
      return data;
    } catch (err) {
      console.error("getUserDashboard error", err?.message || err);
      throw err;
    }
  },

  async getUserPortfolio({ state }) {
    const wallet = state.currentAccount?.address;
    if (!wallet) return null;
    try {
      const { data } = await axios.get(
        getURLFromPieces("users", wallet, "portfolio")
      );
      return data;
    } catch (err) {
      console.error("getUserPortfolio error", err?.message || err);
      throw err;
    }
  },

  async checkAMLFromBackend(
    { state, commit },
    { wallet_address, business_name }
  ) {
    try {
      commit("setIsLoading", true);
      const baseURL = getURLFromPieces(AML_CHECK_ENDPOINT);
      const params = {
        business_name: business_name,
      };
      if (wallet_address) {
        params.wallet_address = wallet_address.toLowerCase();
      }
      const response = await axios.get(baseURL, { params });
      return { aml_check: response.data.aml_check, status: response.status };
    } catch (error) {
      commit("setServerError", error);
    } finally {
      commit("setIsLoading", false);
    }
  },

  async validateAML(
    { commit },
    { business_name, business_incorporation_date }
  ) {
    try {
      commit("setIsLoading", true);
      const baseURL = getURLFromPieces(VALIDATE_AML_ENDPOINT);
      const params = {
        business_name: business_name,
        business_incorporation_date: business_incorporation_date,
      };
      const response = await axios.post(baseURL, params);
      const isAmlVerified = response.data.is_aml_verified;
      const amlData = response.data.aml_data;
      if (amlData) {
        commit("setAmlStatusResponse", amlData);
      }
      return isAmlVerified;
    } catch (error) {
      commit("setServerError", error);
      return false;
    } finally {
      commit("setIsLoading", false);
    }
  },

  async sendEmails(
    { commit },
    {
      business_name,
      business_incorporation_date,
      email,
      full_name,
      project_name,
      project_id,
      wallet_address,
      amount,
    }
  ) {
    try {
      const baseURL = getURLFromPieces(SEND_EMAILS_ENDPOINT);
      const params = {
        business_name: business_name,
        business_incorporation_date: business_incorporation_date,
        email,
        full_name,
        project_name,
        project_id,
        wallet_address,
        amount,
      };
      const response = await axios.post(baseURL, params);
      return response.data;
    } catch (error) {
      commit("setServerError", error);
      return false;
    }
  },
};
